import { Users } from '@platform/client';
import { Icon } from '@platform/components';
import dataTestids from '../constants/test-ids';
import LeftNavPanel from '../dashboard/LeftNavPanel';
import OrganizationDropdown from '../dashboard/OrganizationDropdown';
import ProfileDropdown from '../dashboard/ProfileDropdown';
import FlexibleLink from '../util/FlexibleLink';
import getDashboardSubHeaderText from './getDashboardSubHeaderText';
import { MovingWorldsBannerLogo } from './MovingWorldsLogo';

/** The blue bar at the top of every page containing the MovingWorlds logo */
const PageHeader = ({
  isSidebarOpen,
  setIsSidebarOpen,
  sidebarRef,
}: {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  sidebarRef: React.RefObject<HTMLDivElement>;
}) => {
  const { data: user } = Users.useGetCurrentUser();

  const { navText, cohortUrl, cohortDisplayName } = getDashboardSubHeaderText(user);

  return (
    // The side margins are currently dynamic based on the screen size as a copy of the old app's behavior
    // But, setting this as-is here & in the main page content will be annoying
    // to maintain. Let's make this more reusable
    // TODO: Correct the fixed 54px here; can't use the constant as it's not built with Tailwind
    <div
      className={`z-40 sticky top-0 md:relative md:top-auto bg-brand-500 h-[54px] md:h-[var(--navbar-height)] px-4 lg:px-10 py-6 flex items-center`}
    >
      <div className='text-white'>
        {' '}
        {/* Lazy fix for bad SVG sizing; need to re-export the SVG */}
        <a href={import.meta.env.VITE_LEGACY_APP_URL} data-testid={dataTestids.LAYOUT.HEADER.LOGO}>
          {/* The legacy app uses a different size around roughly the 'md' breakpoint here, but this has been ignored in favor of us redefining how we want to handle mobile responsiveness in the future */}
          <div className='w-[200px] lg:w-[272px]'>
            <MovingWorldsBannerLogo />
          </div>
        </a>
      </div>
      <button
        className='md:hidden ml-auto cursor-pointer mr-[-12px] p-4'
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL_BUTTON}
      >
        <Icon type='menu-02' size='24' stroke='white' />
      </button>

      {isSidebarOpen && (
        <div
          className='md:hidden fixed top-0 left-0 w-full h-screen bg-black bg-opacity-50 z-50'
          data-testid={dataTestids.LAYOUT.HEADER.MOBILE_SIDEPANEL}
        >
          <div
            ref={sidebarRef}
            className='fixed top-0 left-0 overflow-y-auto w-[calc(100vw-64px)] h-screen bg-white z-50'
          >
            <div className='flex flex-col h-fit py-2 gap-2'>
              {(!!navText?.length || !!cohortDisplayName?.length) && (
                <div className='ml-2 mt-2 flex flex-col gap-1'>
                  {!!navText?.length && (
                    <div className='text-lg font-semibold text-gray-700'>{navText}</div>
                  )}
                  {!!cohortDisplayName?.length && (
                    <div className='text-sm font-semibold text-gray-400'>
                      {cohortUrl ?
                        <FlexibleLink
                          className='font-bold underline-offset-8 decoration-2 hover:underline'
                          href={cohortUrl}
                          type='external'
                        >
                          {cohortDisplayName} Cohort
                        </FlexibleLink>
                      : `${cohortDisplayName} Cohort`}
                    </div>
                  )}
                </div>
              )}
              <div>
                <ProfileDropdown />
                {user?.accountType === 'organization' && <OrganizationDropdown />}
                <hr />
                <LeftNavPanel />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageHeader;
